import React from 'react';
import PropTypes from 'prop-types';
import Autocomplete from "react-autocomplete";
import { Tooltip } from 'reactstrap';

const propTypes = {
    autoFocus: PropTypes.bool,       // Focus on input on load
    autoHighlight: PropTypes.bool,   // Default value: true, Whether or not to automatically highlight the top match in the dropdown menu.
    className: PropTypes.string,     // Custom class name to add to input container
    inputErrorMsg: PropTypes.string, // Input inline error message 
    id: PropTypes.string,            // Input id
    isInvalid: PropTypes.func,       // Invokes callback function to return valid input or not
    isSearchButton: PropTypes.bool,  // To show/hide search button 
    itemList: PropTypes.array,       // The items to display in the dropdown menu
    loading: PropTypes.bool,         // Show spinner when items list is fetching
    name: PropTypes.string,          // Input name
    noResults: PropTypes.bool,       // noResults: true, Display no results message
    onChange: PropTypes.func,        // Invoked every time the user changes the input's value.
    onClickPreOpen: PropTypes.func,  // Invoked when the user clicks on pre-open tag. 
    onClickSearch: PropTypes.func,   // Invoked when use click on search button.
    onFocus: PropTypes.func,         // Function triggered on focus
    onSelect: PropTypes.func,        // Invoked when the user selects an item from the dropdown menu.
    placeholder: PropTypes.string,   // Input placeholder text
    preOpen: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),       // Display given string/tag before showing the dropdown menu
    prependIcon: PropTypes.string,   // Prepend Icon to AutoCompleteInput Example: Location Icon
    required: PropTypes.bool,        // Set to true so input empty validation check will trigger on blur and keypress
    selectOnBlur: PropTypes.bool,    // Whether or not to automatically select the highlighted item when the <input> loses focus.
    showAsInvalid: PropTypes.bool,   // Override component validation and display input as invalid
    showClearBtn: PropTypes.bool,    // Display clear input icon button
    showEye: PropTypes.bool,         // Display password entry visibility toggle
    submitForm: PropTypes.func,      // Invokes callback function for valid input
    tooltipID: PropTypes.string,     // Tooltip id which is required to display tooltip message
    tooltipMsg: PropTypes.string,    // Tooltip message
    type: PropTypes.string,          // Input type
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Input value    
};

export default class AutocompleteInput extends React.Component {
    constructor(props) {
        super(props);
        this.toggleToolTip = this.toggleToolTip.bind(this);
        this.onClickPreOpen = this.onClickPreOpen.bind(this);
        this.onClickClear = this.onClickClear.bind(this);
        this.onClickSearch = this.onClickSearch.bind(this);
        this.state = {
            tooltipOpen: false,
            hasFocus: false,
            hasSelect: false,
            enteringSearchText: false,
            invalidInput: false,
            touched: false
        };
    }

    componentDidMount() {
        this._isMounted = true;
        this.isInvalid();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    toggleToolTip() {
        this.setState({ tooltipOpen: !this.state.tooltipOpen });
    }

    onFocus() {
        this.setState({ hasFocus: true });
    }

    onBlur(e) {
        this.setState({
            hasFocus: false, enteringSearchText: false
        }, function () {
            this.isInvalid();
            });

        this.setState({
            touched: true
        });
    }

    onChange(e) {       
        if (this._isMounted) {
            this.props.onChange(e, e.target.value);
        }
    }

    onSelect(val, item) {     
        if (this._isMounted) {
            this.props.onSelect(val, item);
        }

        this.setState({ hasSelect: true, invalidInput: false, enteringSearchText: false });
    }

    commitSelect() {
        const highlightedIndex = this.typeaheadInput.state.highlightedIndex;
        const items = this.typeaheadInput.props.items;
        if (this._isMounted && highlightedIndex) {
            this.props.onSelect(items[highlightedIndex].label, items[highlightedIndex]);
        }
    }

    onKeyDown(e) {
        if (e.key === 'Tab') {
            if (this.state.enteringSearchText) {
                this.commitSelect();
            }
            else {
                this.setState({ hasSelect: false });
            }
            this.setState({ enteringSearchText: false });
        }
        else if (e.key === 'Enter') {
            if (this.state.enteringSearchText) {
                this.commitSelect();
            }
            else {
                if (!this.state.invalidInput) {
                    if (this.props.submitForm) {
                        this.props.submitForm();
                    }
                }
            }
            this.setState({ enteringSearchText: false });
        }
        else {
            this.setState({ hasSelect: false, enteringSearchText: true });
            this.isInvalid();
        }
        //this.onBlur(e);
    }

    onKeyUp = (e) => {
        //this.onBlur(e);
    }

    onPaste() {
        this.setState({ enteringSearchText: true });
    }

    isInvalid() {
        this.setState({
            invalidInput: this.props.required ? !this.props.value : false
        }, function () {
            if (this.props.isInvalid) {
                this.props.isInvalid(this.state.invalidInput);
            }
        });
    }

    onClickPreOpen(preOpen) {
        if (this._isMounted && this.props.onClickPreOpen && preOpen) {
            this.onBlur();
            this.props.onClickPreOpen();
        }
        return true;
    }

    onClickClear() {
        if (this._isMounted) {
            this.props.onChange(null, "");
        }
    }

    onClickSearch(value) {
        if (this._isMounted) {
            if (value)
                this.props.onClickSearch(value);
        }
    }

    render() {
        const {
            placeholder, value, itemList, inputErrorMsg, tooltipID, tooltipMsg, prependIcon,
            autoHighlight, selectOnBlur, loading, noResults, preOpen, isSearchButton, autoFocus,
            className, showAsInvalid, name, id
        } = this.props;
        const inputPropsSettings = {            
            placeholder: placeholder, className: "form-control", autoFocus: autoFocus,
            onFocus: () => this.onFocus(), onBlur: () => this.onBlur(),
            onKeyDown: e => this.onKeyDown(e), onPaste: () => this.onPaste(), onKeyUp: (e) => this.onKeyUp(e),
            "aria-label": placeholder,
            "data-lpignore": true
        };
        const { hasFocus, invalidInput, enteringSearchText, touched } = this.state;
        const containerClass = (value ? " input-not-empty " : " input-empty ") + (className ? " " + className : "") +
            + (hasFocus ? " input-focused " : "") + (((tooltipID && tooltipMsg) || isSearchButton) ? " input-append " : "")
            + (touched ? " input-touched" : " input-untouched ")
            + (invalidInput || showAsInvalid ? " input-invalid" : " input-valid ");
        const openDropdown = enteringSearchText && typeof value !== 'undefined' && value.length > 1;

        return (
            <div className={"il-group autocomplete-group" + containerClass}>
                <Autocomplete wrapperStyle={{ display: 'flex' }}
                    menuStyle={{
                        borderRadius: '0px',
                        boxShadow: '0 1px 7px 0 #C8C8C8',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '0',
                        fontSize: '14px',
                        position: 'fixed',
                        overflow: 'auto',
                        maxHeight: '250px',
                        zIndex: '998'
                    }}
                    inputProps={inputPropsSettings}
                    items={itemList}
                    isItemSelectable={(item) => !item.category || item.label !== item.category}
                    getItemValue={item => item.id}
                    renderItem={(item, highlighted) => {
                        const itemLabel = item.category && item.label === item.category ? "suggestion-category" : "suggestion-item";
                        return (
                            <div className={itemLabel + (highlighted ? ' suggestion-highlighted' : '')} key={item.id}>
                                {item.label}
                            </div>
                        );
                    }}
                    open={openDropdown}
                    autoHighlight={autoHighlight}
                    selectOnBlur={selectOnBlur}
                    value={value}
                    ref={(instance) => (this.typeaheadInput = instance)}
                    onChange={e => this.onChange(e)}
                    onSelect={(value, item) => this.onSelect(value, item)}
                    name={name}
                    id={id || name}
                />
                {tooltipID && tooltipMsg &&
                    <div className="input-icon-append">
                        <span className="icon-info" id={tooltipID} />
                        <Tooltip placement="top" isOpen={this.state.tooltipOpen} autohide={false} target={tooltipID} toggle={this.toggleToolTip}>
                            {tooltipMsg}
                        </Tooltip>
                    </div>
                }
                {isSearchButton &&
                    <div className="input-icon-append">
                        <span className="icon-search" onClick={() => this.onClickSearch(value)} />
                    </div>
                }
                {value && <span className="clear-btn" aria-label="clear button" onClick={() => this.onClickClear()}><span className="icon-input-clear" /></span>}
                <div className="il-label" aria-hidden="true">
                    {prependIcon && <span className={prependIcon} />} {placeholder}
                </div>
                {(invalidInput || showAsInvalid) && value &&
                    <div className="input-error-msg">{inputErrorMsg ? inputErrorMsg : `Please enter a valid ${placeholder}`}</div>
                }
                {preOpen && !noResults && !loading && !value &&
                    <div className="preopen-container" onBlur={(e) => this.onBlur(e)} onClick={(e) => this.onClickPreOpen(preOpen)}>{preOpen}</div>
                }
                {noResults && !loading && hasFocus && value &&
                    <div className="no-results-dropdown">No results </div>
                }
                {loading &&
                    <div className="loading-results-dropdown">
                        <div className="spinner"><div className="bounce1" /><div className="bounce2" /><div className="bounce3" /></div>
                    </div>
                }
            </div>
        );
    }
}

AutocompleteInput.propTypes = propTypes;